import dayjs from "dayjs";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import "./styles.scss";
import { useTranslation } from "react-i18next";

import useRefreshLocationData, {
  RefreshType,
} from "../../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { LocalPostData } from "../../../../models/LocalPostData";
import DataSvc from "../../../../services/dataSvc";
import DemoModeSvc from "../../../../services/demoModeSvc";
import { OptionalRender } from "../../../Common/OptionalRender";
import ModalUpdateGbpLocalPost from "../../../FacilityDetailsComponents/ModalUpdateGbpLocalPost";
import ModalConfirm from "../../../ModalConfirm";
import { RecentMediaListContext } from "../../RecentPostsList";

export interface ILocalPostItemProps {
  localPostItem: LocalPostData;
  isShowLocationName?: boolean;
  context: RecentMediaListContext;
  canModify?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  refreshCallback?: () => void;
  onEdit?: (localPost: LocalPostData) => void;
}

export const LocalPostItem: React.FunctionComponent<ILocalPostItemProps> = (
  props
) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`dashboardPage.localPostItem.${key}`);
  const {
    localPostItem,
    isShowLocationName = true,
    context,
    canModify = false,
    refreshCallback,
    showDeleteButton = false,
    showEditButton = false,
    onEdit,
  } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const refreshData = useRefreshLocationData(
    localPostItem.location_id.toString()
  );

  const hasMedia =
    localPostItem.media &&
    Array.isArray(localPostItem.media) &&
    localPostItem.media.length > 0;

  const mediaUrl = hasMedia ? localPostItem.media[0]?.googleUrl : "";

  const handleConfirmDeleteLocalPost = () => {
    setShowDeleteModal(true);
  };

  const handleCloseConfirmDeleteLocalPost = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteLocalPost = async () => {
    try {
      await DataSvc.deleteLocalPost(
        localPostItem.location_id.toString(),
        localPostItem.id.toString()
      );

      setShowDeleteModal(false);

      refreshData(RefreshType.LOCAL_POSTS);

      if (refreshCallback) {
        refreshCallback();
      }
    } catch {
      // Do nothing, axios will handle the error
    }
  };

  const handleEditLocalPost = () => {
    setShowUpdateModal(true);
    ReactGA.event({
      category: "local-post",
      action: "Edit GBP Local Post",
    });
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);

    refreshData(RefreshType.LOCAL_POSTS);

    if (refreshCallback) {
      refreshCallback();
    }
  };

  return (
    <>
      <div className="local-post-item">
        <a
          key={localPostItem.id}
          href={localPostItem?.searchUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            ReactGA.event({
              category: "dashboard",
              action: "Open Local Post",
              label: context,
            });
          }}
        >
          <div className="local-post-top-info">
            <div className="left-title">
              {isShowLocationName ? localPostItem.location_name : ""}
            </div>
            <div className="right-title">
              {dayjs(localPostItem.post_date).format("YYYY-MM-DD")}
            </div>
          </div>
          <div className="local-post-bottom-container">
            <div className="local-post-img-container">
              {mediaUrl ? (
                <img
                  className="local-post-img"
                  alt="local-post"
                  src={mediaUrl}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="no-image">
                  <div>
                    {DemoModeSvc.current.isDemoModeOn
                      ? "デモ"
                      : t("no_picture")}
                  </div>
                </div>
              )}
            </div>
            {localPostItem.summary ? (
              <div className="summary">{localPostItem.summary}</div>
            ) : (
              <div className="no-summary">{t("no_comment")}</div>
            )}
          </div>
        </a>
        {canModify && (showEditButton || showDeleteButton) && (
          <div className="local-post-item-controls-container">
            {/* This feature was developed as part of a grant for internal-use features only.
          This feature can be made public after the grant is complete. */}
            <OptionalRender mustBeImpersonating>
              {showEditButton && (
                <button
                  className="btn btn-border"
                  onClick={
                    onEdit ? () => onEdit(localPostItem) : handleEditLocalPost
                  }
                  type="button"
                >
                  {t("edit")}
                </button>
              )}
            </OptionalRender>
            {showDeleteButton && (
              <button
                className="btn btn-red"
                onClick={handleConfirmDeleteLocalPost}
                type="button"
              >
                {t("delete")}
              </button>
            )}
          </div>
        )}
      </div>
      {showDeleteModal && (
        <ModalConfirm
          title={t("deleteModal.title")}
          cancelLabel={t("deleteModal.cancel")}
          confirmLabel={t("deleteModal.delete")}
          onClose={handleCloseConfirmDeleteLocalPost}
          onConfirm={handleDeleteLocalPost}
          translate={false}
          color="red"
        >
          <div className="delete-modal-local-post-preview-container">
            <LocalPostItem
              key={localPostItem.id}
              localPostItem={localPostItem}
              isShowLocationName={true}
              context={RecentMediaListContext.OWN_LOCATION_HISTORY}
            />
          </div>
        </ModalConfirm>
      )}

      {showUpdateModal && (
        <ModalUpdateGbpLocalPost
          onClose={() => setShowUpdateModal(false)}
          localPost={localPostItem}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </>
  );
};

export default LocalPostItem;
